import { ComponentProps, FocusEvent, forwardRef, useEffect, useRef, useState } from "react";
import { autoUpdate, useFloating } from "@floating-ui/react";
import { Input, FloatingControls, useFloatingControls, mergeRefs } from "@theorgchartdev/storybook";

type InputProps = ComponentProps<typeof Input>;

type ValidatorFn = (value: string) => {
  result: boolean;
  msg?: string;
  status?: "default" | "success" | "error";
};

type FloatingProps = {
  onConfirm?: (value: string) => void;
  onCancel?: () => void;
  align?: "left" | "right";
  validatorFn?: ValidatorFn;
  saveOnBlur?: boolean;
};

export type InputWithFloatingControlsProps = Omit<
  InputProps,
  "value" | "defaultValue" | "onChange"
> &
  FloatingProps & {
    defaultValue?: string;
    value?: string;
    onChange?: InputProps["onChange"];
  };

const InputWithFloatingControls = forwardRef<HTMLInputElement, InputWithFloatingControlsProps>(
  (
    {
      onConfirm,
      onCancel,
      align = "right",
      validatorFn,
      saveOnBlur = false,
      onChange: onChangeProp,
      onKeyDown: onKeyDownProp,
      onBlur: onBlurProp,
      value,
      defaultValue,
      ...rest
    },
    ref
  ) => {
    const initialValue = value ?? defaultValue ?? "";
    const { draft, setDraft, confirm, cancel } = useFloatingControls(
      initialValue,
      onConfirm ?? (() => {})
    );

    // Validation State
    const [validResult, setValidResult] = useState(
      () => !validatorFn || validatorFn(initialValue).result
    );
    const [internalStatus, setInternalStatus] = useState<
      "default" | "success" | "error" | "warning"
    >("default");
    const [internalMessage, setInternalMessage] = useState("");
    const validate = (val: string): boolean => {
      if (!validatorFn) return true;
      const result = validatorFn(val);
      setValidResult(result.result);
      setInternalStatus(result.status ?? (result.result ? "default" : "error"));
      setInternalMessage(result.msg ?? "");
      return result.result;
    };

    useEffect(() => {
      validate(initialValue);
    }, []);

    // Positioning
    const inputRef = useRef<HTMLInputElement>(null);
    const floatingContainerRef = useRef<HTMLDivElement>(null);
    const { refs: floatingRefs, floatingStyles } = useFloating({
      strategy: "fixed",
      placement: align === "right" ? "bottom-end" : "bottom-start",
      whileElementsMounted: autoUpdate
    });

    const handleConfirm = () => {
      if (!validate(draft)) return;
      confirm();
    };

    const handleCancel = () => {
      validate(initialValue);
      cancel();
      onCancel?.();
    };

    // Blur guard: only act when focus truly leaves both the input and the
    // floating container (e.g. moving from input → confirm button is not an exit).
    const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
      if (floatingContainerRef.current?.contains(event.relatedTarget as Node)) return;
      saveOnBlur ? handleConfirm() : handleCancel();
    };

    const handleContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
      const focusStayed =
        event.relatedTarget === inputRef.current ||
        floatingContainerRef.current?.contains(event.relatedTarget as Node);
      if (focusStayed) return;
      saveOnBlur ? handleConfirm() : handleCancel();
    };

    return (
      <>
        <Input
          ref={mergeRefs(ref, inputRef, floatingRefs.setReference)}
          {...rest}
          value={draft}
          onKeyDown={event => {
            if (event.key === "Enter") {
              handleConfirm();
              event.preventDefault();
            } else if (event.key === "Escape") {
              handleCancel();
              event.preventDefault();
            }
            onKeyDownProp?.(event);
          }}
          onBlur={event => {
            handleInputBlur(event);
            onBlurProp?.(event);
          }}
          onChange={(event, value) => {
            const val =
              typeof value === "string" ? value : (event.target as HTMLInputElement).value;
            setDraft(val);
            validate(val);
            onChangeProp?.(event, value);
          }}
          {...(validatorFn && {
            status: internalStatus,
            message: internalMessage
          })}
        />
        <div
          ref={mergeRefs(floatingRefs.setFloating, floatingContainerRef)}
          style={{ ...floatingStyles, zIndex: 1000 }}
          onBlur={handleContainerBlur}
        >
          <FloatingControls
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            handleBlurBackward={() => inputRef.current?.focus()}
            disableConfirm={!validResult}
            align={align}
          />
        </div>
      </>
    );
  }
);

InputWithFloatingControls.displayName = "InputWithFloatingControls";

export default InputWithFloatingControls;
